// Sugalarm — Cycle 456: "Who's treating Sam?" — the picker's rules.
//
// Pure decisions (unit-tested in `attributionPicker.test.ts`) plus two tiny
// module stores, the `nativeCamera.ts` shape:
//
//   * the ALARM-SOUNDING gate App.tsx publishes (any alarm audio on this
//     device: the episode's own audio, the low-side bridge, the No Data
//     tone) — the picker closes the moment it rises;
//   * the PICKER-OPEN flag the frame reads to dim the kiosk rail.
//
// ⚠ THE PICKER IS AN HONOUR-SYSTEM LABEL AND GATES NOTHING. It opens only
// AFTER "I'm treating this" has succeeded and the window is open, it never
// opens on a snooze and never while audio sounds, and nothing it does can
// open, extend, close or quiet a window. A failure says so in its own footer
// and changes nothing else.

import { useSyncExternalStore } from "react";

import type { HeroRosterItem, TreatingBy, TreatingBySource } from "./types";

// ── when it opens ──────────────────────────────────────────────────────

export interface PickerOpenInput {
  /** `episode.treating` — a DECLARED window is open (never a snooze). */
  declaredOpen: boolean;
  /** The open window's identity (`custody_opened_at`). */
  windowId: string | null;
  /** `episode.treatingBy` — already attributed ⇒ no question. */
  treatingBy: TreatingBy | null;
  /** This device's own tap opened (or re-tapped) the window. */
  tappedHere: boolean;
  /** The signed-in account is linked to a hero on this member's list
   *  (phones and web only; a kiosk is never linked). The server attributes
   *  that tap itself, so the device asks nothing. */
  linkedHere: boolean;
  /** Any alarm audio is sounding on this device right now. */
  alarmSounding: boolean;
  /** Safety review C3: a dark feed or a held ascent keeps the main area
   *  (and its lines) on screen, so the picker does not open over it. */
  feedDark?: boolean;
  ascentLatched?: boolean;
}

/** May the picker open for this window now? */
export function pickerMayOpen(i: PickerOpenInput): boolean {
  return (
    i.declaredOpen &&
    i.windowId != null &&
    i.treatingBy == null &&
    i.tappedHere &&
    !i.linkedHere &&
    !i.alarmSounding &&
    i.feedDark !== true &&
    i.ascentLatched !== true
  );
}

export interface PickerCloseInput {
  /** The window the picker was opened for. */
  openedFor: string | null;
  declaredOpen: boolean;
  windowId: string | null;
  alarmSounding: boolean;
  /** A check-in / worsening / still-treating question is up — a prompt is
   *  an alarm-path control and outranks the picker. */
  promptPending: boolean;
  /** Safety review C3: the feed went dark (No Data) or the ascent latch is
   *  holding low alerts. Each has its own line on the treating screen's
   *  main area, which the picker covers — so the picker steps aside. */
  feedDark?: boolean;
  ascentLatched?: boolean;
}

/** Must an open picker close now (other than by a pick, Skip or the 45 s
 *  timeout)? The window closed or changed, alarm audio started, or a
 *  prompt came up. */
export function pickerMustClose(i: PickerCloseInput): boolean {
  return (
    !i.declaredOpen ||
    i.windowId == null ||
    i.windowId !== i.openedFor ||
    i.alarmSounding ||
    i.promptPending ||
    i.feedDark === true ||
    i.ascentLatched === true
  );
}

/** One step of the "this device tapped, ask who once its window shows"
 *  latch (astra reviews, rounds 1 and 2). The latch knows the window its
 *  OWN successful tap opened (`tapWindow`, from the POST response) and
 *  asks about that window and no other:
 *   - the frame shows `tapWindow` open → "decide" (or "seen-wait" while
 *     audio sounds — never before audio has stopped);
 *   - a NEWER window, or `tapWindow` closed after it was seen → "drop";
 *   - frames still lagging (no window yet, or an OLDER one) → "wait".
 *  With no `tapWindow` (an older server) it binds to the first open window
 *  it sees, the round-1 rule. */
export type PickerLatchStep = "wait" | "seen-wait" | "drop" | "decide";

export function pickerLatchStep(i: {
  tapWindow: string | null;
  seen: boolean;
  declaredOpen: boolean;
  windowId: string | null;
  alarmSounding: boolean;
}): PickerLatchStep {
  const target = i.tapWindow;
  if (target == null) {
    if (!i.declaredOpen || i.windowId == null) return i.seen ? "drop" : "wait";
    return i.alarmSounding ? "seen-wait" : "decide";
  }
  if (i.declaredOpen && i.windowId === target) {
    return i.alarmSounding ? "seen-wait" : "decide";
  }
  if (i.windowId != null && i.windowId !== target) {
    const newer = Date.parse(i.windowId) > Date.parse(target);
    return newer || i.seen ? "drop" : "wait";
  }
  return i.seen ? "drop" : "wait";
}

// ── paging ─────────────────────────────────────────────────────────────

/** Six per page on the kiosk and web (3 × 2) and on a portrait phone
 *  (2 × 3); three per page on a landscape phone (3 × 1). Nothing gets
 *  smaller with more heroes — they page. */
export function pickerPageSize(
  variant: "kiosk" | "web" | "mobile",
  landscape: boolean,
): number {
  if (variant === "mobile" && landscape) return 3;
  return 6;
}

/** How many tiles a page holds when only `available` px of height are
 *  free for the grid: whole rows of `cols`, at most `fullPage`, at least
 *  one row. When the heroes need more than one page, `pagerPx` is taken
 *  off first for the pager row. `available <= 0` (not yet laid out) keeps
 *  the full page. Tiles never shrink — a short screen pages more. */
export function pickerRowsThatFit(i: {
  available: number;
  rowPx: number;
  gapPx: number;
  pagerPx: number;
  cols: number;
  fullPage: number;
  heroCount: number;
}): number {
  if (!(i.available > 0)) return i.fullPage;
  const rows = (h: number) => Math.max(1, Math.floor((h + i.gapPx) / (i.rowPx + i.gapPx)));
  let perPage = Math.min(i.fullPage, rows(i.available) * i.cols);
  if (i.heroCount > perPage) {
    perPage = Math.min(i.fullPage, rows(i.available - i.pagerPx) * i.cols);
  }
  return Math.max(i.cols, perPage);
}

export function pickerPageCount(heroCount: number, pageSize: number): number {
  return Math.max(1, Math.ceil(heroCount / Math.max(1, pageSize)));
}

export function pickerPage(
  heroes: readonly HeroRosterItem[],
  page: number,
  pageSize: number,
): HeroRosterItem[] {
  const count = pickerPageCount(heroes.length, pageSize);
  const p = Math.max(0, Math.min(count - 1, page));
  return heroes.slice(p * pageSize, p * pageSize + pageSize);
}

// ── copy ───────────────────────────────────────────────────────────────

export function pickerTitle(memberName: string | null | undefined): string {
  const name = (memberName ?? "").trim();
  return name ? `Who's treating ${name}?` : "Who's treating?";
}

export function pickerCountdownLine(secondsLeft: number): string {
  return `Closes on its own in ${Math.max(0, Math.ceil(secondsLeft))}s. The timer keeps running either way.`;
}

export function pickerConfirmation(name: string | null): string {
  return name
    ? `Thanks ${name}. Everyone can see you have this.`
    : "Thanks. Everyone can see someone has this.";
}

/** "11:26" — household local time (this device's clock). */
export function clockTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const h = d.getHours() % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const SOURCE_WHERE: Record<TreatingBySource, string> = {
  kiosk_pick: "on the kiosk",
  phone_pick: "from a phone",
  linked_account: "from a phone",
  keypress: "on a call",
  someone_else: "",
};

/** The attributed card's second line on every OTHER surface:
 *  "Took it at 11:26 on the kiosk". */
export function tookItLine(by: TreatingBy | null): string | null {
  if (!by || by.hero_id == null) return null;
  const when = clockTime(by.at);
  const where = SOURCE_WHERE[by.source] ?? "";
  if (!when) return null;
  return where ? `Took it at ${when} ${where}` : `Took it at ${when}`;
}

/** "Maya · started 11:26" — the treatment timer card's line. */
export function startedLine(
  by: TreatingBy | null,
  openedAt: string | null,
): string | null {
  if (!by || by.hero_id == null || !by.display_name) return null;
  const when = clockTime(openedAt);
  return when ? `${by.display_name} · started ${when}` : by.display_name;
}

/** Is this the treater's own device? (the linked account that took it) */
export function isSelfTreating(
  by: TreatingBy | null,
  myContactId: string | null | undefined,
): boolean {
  return by != null && by.hero_id != null && myContactId != null && by.hero_id === myContactId;
}

// ── stores ─────────────────────────────────────────────────────────────

function makeFlag() {
  let value = false;
  const listeners = new Set<() => void>();
  return {
    set(next: boolean) {
      if (value === next) return;
      value = next;
      listeners.forEach((l) => l());
    },
    get: () => value,
    subscribe(l: () => void) {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
  };
}

const alarmSounding = makeFlag();
const pickerOpen = makeFlag();

/** App.tsx publishes "any alarm audio is sounding on this device". */
export function setAlarmSoundingForPicker(sounding: boolean): void {
  alarmSounding.set(sounding);
}

export function alarmSoundingForPicker(): boolean {
  return alarmSounding.get();
}

export function useAlarmSoundingForPicker(): boolean {
  return useSyncExternalStore(alarmSounding.subscribe, alarmSounding.get, () => false);
}

/** The picker publishes whether it is up, so the kiosk rail can dim. */
export function setAttributionPickerOpen(open: boolean): void {
  pickerOpen.set(open);
}

export function useAttributionPickerOpen(): boolean {
  return useSyncExternalStore(pickerOpen.subscribe, pickerOpen.get, () => false);
}
